'use client';

import React, { CSSProperties, useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, ChevronUp, Copy, Download, Pencil, RotateCcw, Save } from "lucide-react";

export type ProductPricing = {
    label?: string;
    price?: number | null;
};

export type ProductDimensions = {
    width?: string;
    height?: string;
    depth?: string;
};

export type ProductImage = {
    id?: string;
    original?: string;
    thumbnail?: string;
    alt?: string;
};

export type ProductData = {
    id?: string;
    parentId?: string;
    slug?: string;
    title?: string;
    description?: string;
    searchTerms?: string[];
    features?: string[];
    basic?: {
        name?: string;
        subtitle?: string;
        category?: string;
    };
    pricing?: ProductPricing[];
    attributes?: {
        dimensions?: ProductDimensions;
    };
    layout?: {
        type?: string;
        maxWidth?: string;
    };
    pageMeta?: {
        status?: string;
        createdAt?: string;
        updatedAt?: string;
        schema?: {
            type?: string;
            faq?: boolean;
            howTo?: boolean;
        };
    };
    content?: Record<string, unknown>;
    featureImage?: {
        original?: string;
        thumbnail?: string;
        alt?: string;
    };
    images?: ProductImage[];
    seo?: {
        title?: string;
        canonicalUrl?: string;
        ogImage?: string;
        type?: string;
    };
    sortOrder?: number;
    metaData?: {
        productId?: string;
        inventoryId?: string;
    };
};

export type ProductMiniEditorCardProps = {
    product: ProductData;
    className?: string;
    currency?: string;
    defaultExpanded?: boolean;
    onChange?: (product: ProductData) => void;
    onSave?: (product: ProductData) => void;
    showImage?: boolean;
};

const formatPrice = (price?: number | null, currency: string = "AUD") => {
    if (price === null || price === undefined || price <= 0) {
        return "Contact";
    }

    return new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency,
        maximumFractionDigits: 0,
    }).format(price);
};

const cloneProduct = (product: ProductData): ProductData =>
    JSON.parse(JSON.stringify(product || {}));

const normaliseProduct = (product: ProductData): ProductData => ({
    id: product?.id || "",
    parentId: product?.parentId || "",
    slug: product?.slug || "",
    title: product?.title || "",
    description: product?.description || "",
    searchTerms: Array.isArray(product?.searchTerms) ? product.searchTerms : [],
    features: Array.isArray(product?.features) ? product.features : [],
    basic: {
        name: product?.basic?.name || "",
        subtitle: product?.basic?.subtitle || "",
        category: product?.basic?.category || "",
    },
    pricing: Array.isArray(product?.pricing) ? product.pricing : [],
    attributes: {
        dimensions: {
            width: product?.attributes?.dimensions?.width || "",
            height: product?.attributes?.dimensions?.height || "",
            depth: product?.attributes?.dimensions?.depth || "",
        },
    },
    layout: {
        type: product?.layout?.type || "",
        maxWidth: product?.layout?.maxWidth || "",
    },
    pageMeta: {
        status: product?.pageMeta?.status || "",
        createdAt: product?.pageMeta?.createdAt || "",
        updatedAt: product?.pageMeta?.updatedAt || "",
        schema: {
            type: product?.pageMeta?.schema?.type || "",
            faq: Boolean(product?.pageMeta?.schema?.faq),
            howTo: Boolean(product?.pageMeta?.schema?.howTo),
        },
    },
    content: product?.content || {},
    featureImage: {
        original: product?.featureImage?.original || "",
        thumbnail: product?.featureImage?.thumbnail || "",
        alt: product?.featureImage?.alt || "",
    },
    images: Array.isArray(product?.images) ? product.images : [],
    seo: {
        title: product?.seo?.title || "",
        canonicalUrl: product?.seo?.canonicalUrl || "",
        ogImage: product?.seo?.ogImage || "",
        type: product?.seo?.type || "",
    },
    sortOrder: product?.sortOrder ?? 0,
    metaData: {
        productId: product?.metaData?.productId || "",
        inventoryId: product?.metaData?.inventoryId || "",
    },
});

const formatCategoryLabel = (value?: string) => {
    if (!value) return "";

    return value
        .replace(/^itm-prd-/, "")
        .replace(/-/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

export default function ProductMiniEditorCard({
                                                  product,
                                                  className = "",
                                                  currency = "AUD",
                                                  defaultExpanded = false,
                                                  onChange,
                                                  onSave,
                                                  showImage = true,
                                              }: ProductMiniEditorCardProps) {
    const [expanded, setExpanded] = useState(defaultExpanded);
    const [copied, setCopied] = useState(false);
    const [editableProduct, setEditableProduct] = useState<ProductData>(() =>
        normaliseProduct(cloneProduct(product))
    );

    useEffect(() => {
        setEditableProduct(normaliseProduct(cloneProduct(product)));
    }, [product]);

    const title = editableProduct?.basic?.name || editableProduct?.title || "Untitled Product";
    const subtitle = editableProduct?.basic?.subtitle || "";
    const category = editableProduct?.parentId || editableProduct?.basic?.category || "";
    const categoryLabel = formatCategoryLabel(category);
    const featureImage =
        editableProduct?.featureImage?.thumbnail ||
        editableProduct?.featureImage?.original ||
        "";
    const pricing = editableProduct?.pricing || [];
    const mainPrice = pricing[0];
    const dimensions = editableProduct?.attributes?.dimensions || {};
    const features = editableProduct?.features || [];

    const jsonOutput = useMemo(
        () => JSON.stringify(normaliseProduct(editableProduct), null, 2),
        [editableProduct]
    );

    const emitChange = (next: ProductData) => {
        setEditableProduct(next);
        onChange?.(next);
    };

    const updateProduct = (updater: (current: ProductData) => ProductData) => {
        const next = normaliseProduct(updater(cloneProduct(editableProduct)));
        emitChange(next);
    };

    const setBasicField = (field: "name" | "subtitle", value: string) => {
        updateProduct((current) => ({
            ...current,
            basic: {
                ...current.basic,
                [field]: value,
            },
        }));
    };

    const setDescription = (value: string) => {
        updateProduct((current) => ({
            ...current,
            description: value,
        }));
    };

    const setDimensionField = (field: keyof ProductDimensions, value: string) => {
        updateProduct((current) => ({
            ...current,
            attributes: {
                ...current.attributes,
                dimensions: {
                    ...current.attributes?.dimensions,
                    [field]: value,
                },
            },
        }));
    };

    const setFeatureImageField = (field: "original" | "thumbnail" | "alt", value: string) => {
        updateProduct((current) => ({
            ...current,
            featureImage: {
                ...current.featureImage,
                [field]: value,
            },
        }));
    };

    const updatePricingItem = (index: number, field: keyof ProductPricing, value: string) => {
        updateProduct((current) => {
            const nextPricing = [...(current.pricing || [])];
            const existing = nextPricing[index] || {};

            nextPricing[index] = {
                ...existing,
                [field]:
                    field === "price"
                        ? value.trim() === ""
                            ? null
                            : Number(value)
                        : value,
            };

            return {
                ...current,
                pricing: nextPricing,
            };
        });
    };

    const addPricingItem = () => {
        updateProduct((current) => ({
            ...current,
            pricing: [...(current.pricing || []), { label: "", price: null }],
        }));
    };

    const removePricingItem = (index: number) => {
        updateProduct((current) => ({
            ...current,
            pricing: (current.pricing || []).filter((_, i) => i !== index),
        }));
    };

    const updateFeature = (index: number, value: string) => {
        updateProduct((current) => {
            const nextFeatures = [...(current.features || [])];
            nextFeatures[index] = value;

            return {
                ...current,
                features: nextFeatures,
            };
        });
    };

    const addFeature = () => {
        updateProduct((current) => ({
            ...current,
            features: [...(current.features || []), ""],
        }));
    };

    const removeFeature = (index: number) => {
        updateProduct((current) => ({
            ...current,
            features: (current.features || []).filter((_, i) => i !== index),
        }));
    };

    const handleReset = () => {
        const next = normaliseProduct(cloneProduct(product));
        emitChange(next);
    };

    const handleCopyJson = async () => {
        try {
            await navigator.clipboard.writeText(jsonOutput);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1800);
        } catch (error) {
            console.error("Failed to copy JSON", error);
        }
    };

    const handleDownloadJson = () => {
        try {
            const blob = new Blob([jsonOutput], { type: "application/json;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const anchor = document.createElement("a");

            const safeId =
                (editableProduct?.id || "")
                    .toLowerCase()
                    .replace(/[^a-z0-9-_]+/g, "-")
                    .replace(/^-+|-+$/g, "");

            const fallbackName =
                (editableProduct?.basic?.name || editableProduct?.title || "product")
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, "-")
                    .replace(/^-+|-+$/g, "") || "product";

            anchor.href = url;
            anchor.download = `${safeId || fallbackName}.json`;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Failed to download JSON", error);
        }
    };

    const handleSave = () => {
        onSave?.(normaliseProduct(cloneProduct(editableProduct)));
    };

    const styles: Record<string, CSSProperties> = {
        root: {
            width: "100%",
            border: "1px solid rgba(17,17,17,0.08)",
            borderRadius: 18,
            background: "#ffffff",
            boxShadow: "0 6px 24px rgba(0,0,0,0.04)",
            overflow: "hidden",
            fontFamily:
                'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        },
        summaryRow: {
            display: "grid",
            gridTemplateColumns: showImage ? "88px minmax(0, 1fr) auto" : "minmax(0, 1fr) auto",
            gap: 16,
            alignItems: "center",
            padding: 16,
        },
        imageWrap: {
            width: 88,
            height: 88,
            borderRadius: 14,
            overflow: "hidden",
            border: "1px solid rgba(17,17,17,0.08)",
            background: "#f7f7f7",
            flexShrink: 0,
        },
        image: {
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
        },
        titleWrap: {
            display: "grid",
            gap: 8,
            minWidth: 0,
        },
        categoryPill: {
            display: "inline-flex",
            alignItems: "center",
            width: "fit-content",
            minHeight: 24,
            padding: "4px 8px",
            borderRadius: 999,
            background: "rgba(17,17,17,0.04)",
            border: "1px solid rgba(17,17,17,0.08)",
            fontSize: 11,
            lineHeight: 1,
            fontWeight: 700,
            color: "rgba(17,17,17,0.58)",
            textTransform: "uppercase",
            letterSpacing: "0.08em",
        },
        title: {
            margin: 0,
            fontSize: 18,
            lineHeight: 1.2,
            fontWeight: 700,
            color: "#111111",
        },
        subtitle: {
            margin: 0,
            fontSize: 13,
            lineHeight: 1.4,
            color: "rgba(17,17,17,0.66)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        },
        metaRow: {
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
        },
        metaChip: {
            display: "inline-flex",
            alignItems: "center",
            minHeight: 30,
            padding: "6px 10px",
            borderRadius: 999,
            background: "#fafafa",
            border: "1px solid rgba(17,17,17,0.08)",
            fontSize: 12,
            lineHeight: 1.2,
            fontWeight: 600,
            color: "#111111",
        },
        actions: {
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
            justifyContent: "flex-end",
        },
        button: {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            minHeight: 38,
            padding: "8px 12px",
            borderRadius: 12,
            border: "1px solid rgba(17,17,17,0.12)",
            background: "#ffffff",
            color: "#111111",
            fontSize: 13,
            fontWeight: 700,
            cursor: "pointer",
        },
        buttonPrimary: {
            background: "#111111",
            color: "#ffffff",
            border: "1px solid #111111",
        },
        editPanel: {
            borderTop: "1px solid rgba(17,17,17,0.08)",
            padding: 16,
            background: "#fcfcfc",
            display: "grid",
            gap: 16,
        },
        editTopActions: {
            display: "flex",
            flexWrap: "wrap",
            gap: 10,
            alignItems: "center",
            justifyContent: "space-between",
        },
        grid: {
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 12,
        },
        full: {
            gridColumn: "1 / -1",
        },
        field: {
            display: "grid",
            gap: 8,
        },
        label: {
            fontSize: 12,
            lineHeight: 1.2,
            fontWeight: 700,
            color: "rgba(17,17,17,0.6)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
        },
        input: {
            width: "100%",
            minHeight: 40,
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid rgba(17,17,17,0.12)",
            background: "#ffffff",
            color: "#111111",
            fontSize: 14,
            boxSizing: "border-box",
        },
        textarea: {
            width: "100%",
            minHeight: 90,
            padding: "10px 12px",
            borderRadius: 12,
            border: "1px solid rgba(17,17,17,0.12)",
            background: "#ffffff",
            color: "#111111",
            fontSize: 14,
            lineHeight: 1.5,
            resize: "vertical",
            boxSizing: "border-box",
        },
        repeaterRow: {
            display: "grid",
            gap: 8,
            padding: 12,
            borderRadius: 14,
            border: "1px solid rgba(17,17,17,0.08)",
            background: "#ffffff",
        },
        jsonBox: {
            width: "100%",
            minHeight: 220,
            padding: 14,
            borderRadius: 14,
            border: "1px solid rgba(17,17,17,0.12)",
            background: "#0f172a",
            color: "#e2e8f0",
            fontSize: 13,
            lineHeight: 1.5,
            fontFamily:
                'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            resize: "vertical",
            boxSizing: "border-box",
        },
    };

    return (
        <section style={styles.root} className={className}>
            <style>{`
                @media (max-width: 900px) {
                    .product-mini-editor-grid {
                        grid-template-columns: 1fr !important;
                    }
                }

                @media (max-width: 700px) {
                    .product-mini-editor-summary {
                        grid-template-columns: 1fr !important;
                    }

                    .product-mini-editor-actions {
                        justify-content: flex-start !important;
                    }
                }
            `}</style>

            <div className="product-mini-editor-summary" style={styles.summaryRow}>
                {showImage ? (
                    <div style={styles.imageWrap}>
                        {featureImage ? (
                            <img
                                src={featureImage}
                                alt={editableProduct?.featureImage?.alt || title}
                                style={styles.image}
                            />
                        ) : null}
                    </div>
                ) : null}

                <div style={styles.titleWrap}>
                    {categoryLabel ? <div style={styles.categoryPill}>{categoryLabel}</div> : null}
                    <h3 style={styles.title}>{title}</h3>
                    {subtitle ? <p style={styles.subtitle}>{subtitle}</p> : null}

                    <div style={styles.metaRow}>
                        <span style={styles.metaChip}>
                            {mainPrice ? `${mainPrice.label || "Price"}: ${formatPrice(mainPrice.price, currency)}` : "No price"}
                        </span>

                        {(dimensions.width || dimensions.height || dimensions.depth) ? (
                            <span style={styles.metaChip}>
                                {[
                                    dimensions.width ? `W ${dimensions.width}` : null,
                                    dimensions.height ? `H ${dimensions.height}` : null,
                                    dimensions.depth ? `D ${dimensions.depth}` : null,
                                ]
                                    .filter(Boolean)
                                    .join(" · ")}
                            </span>
                        ) : null}

                        <span style={styles.metaChip}>
                            {features.length} feature{features.length === 1 ? "" : "s"}
                        </span>
                    </div>
                </div>

                <div className="product-mini-editor-actions" style={styles.actions}>
                    <button
                        type="button"
                        onClick={() => setExpanded((prev) => !prev)}
                        style={styles.button}
                    >
                        <Pencil size={15} />
                        <span>{expanded ? "Close" : "Edit"}</span>
                        {expanded ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                    </button>

                    {/*<button*/}
                    {/*    type="button"*/}
                    {/*    onClick={handleSave}*/}
                    {/*    style={{ ...styles.button, ...styles.buttonPrimary }}*/}
                    {/*>*/}
                    {/*    <Save size={15} />*/}
                    {/*    <span>Save Hook</span>*/}
                    {/*</button>*/}
                </div>
            </div>

            {expanded ? (
                <div style={styles.editPanel}>
                    <div style={styles.editTopActions}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                            <button
                                type="button"
                                onClick={handleDownloadJson}
                                style={{ ...styles.button, ...styles.buttonPrimary }}
                            >
                                <Download size={15} />
                                <span>Download JSON</span>
                            </button>

                            <button
                                type="button"
                                onClick={handleCopyJson}
                                style={styles.button}
                            >
                                {copied ? <Check size={15} /> : <Copy size={15} />}
                                <span>{copied ? "Copied" : "Copy JSON"}</span>
                            </button>

                            <button
                                type="button"
                                onClick={handleReset}
                                style={styles.button}
                            >
                                <RotateCcw size={15} />
                                <span>Reset</span>
                            </button>
                        </div>
                    </div>

                    <div className="product-mini-editor-grid" style={styles.grid}>
                        <div style={styles.field}>
                            <label style={styles.label}>Name</label>
                            <input
                                value={editableProduct.basic?.name || ""}
                                onChange={(e) => setBasicField("name", e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        <div style={styles.field}>
                            <label style={styles.label}>Subtitle</label>
                            <input
                                value={editableProduct.basic?.subtitle || ""}
                                onChange={(e) => setBasicField("subtitle", e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        <div style={{ ...styles.field, ...styles.full }}>
                            <label style={styles.label}>Pricing</label>
                            <div style={{ display: "grid", gap: 10 }}>
                                {(editableProduct.pricing || []).map((item, index) => (
                                    <div key={`pricing-${index}`} style={styles.repeaterRow}>
                                        <input
                                            placeholder="Label"
                                            value={item.label || ""}
                                            onChange={(e) => updatePricingItem(index, "label", e.target.value)}
                                            style={styles.input}
                                        />
                                        <input
                                            placeholder="Price"
                                            type="number"
                                            value={
                                                item.price === null || item.price === undefined
                                                    ? ""
                                                    : String(item.price)
                                            }
                                            onChange={(e) => updatePricingItem(index, "price", e.target.value)}
                                            style={styles.input}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removePricingItem(index)}
                                            style={styles.button}
                                        >
                                            Remove Price
                                        </button>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={addPricingItem}
                                    style={styles.button}
                                >
                                    Add Price
                                </button>
                            </div>
                        </div>

                        <div style={{ ...styles.field, ...styles.full }}>
                            <label style={styles.label}>Description</label>
                            <textarea
                                value={editableProduct.description || ""}
                                onChange={(e) => setDescription(e.target.value)}
                                style={styles.textarea}
                            />
                        </div>

                        <div style={styles.field}>
                            <label style={styles.label}>Feature Image Original</label>
                            <input
                                value={editableProduct.featureImage?.original || ""}
                                onChange={(e) => setFeatureImageField("original", e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        <div style={styles.field}>
                            <label style={styles.label}>Feature Image Thumbnail</label>
                            <input
                                value={editableProduct.featureImage?.thumbnail || ""}
                                onChange={(e) => setFeatureImageField("thumbnail", e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        <div style={{ ...styles.field, ...styles.full }}>
                            <label style={styles.label}>Feature Image Alt</label>
                            <input
                                value={editableProduct.featureImage?.alt || ""}
                                onChange={(e) => setFeatureImageField("alt", e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        <div style={styles.field}>
                            <label style={styles.label}>Width</label>
                            <input
                                value={editableProduct.attributes?.dimensions?.width || ""}
                                onChange={(e) => setDimensionField("width", e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        <div style={styles.field}>
                            <label style={styles.label}>Height</label>
                            <input
                                value={editableProduct.attributes?.dimensions?.height || ""}
                                onChange={(e) => setDimensionField("height", e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        <div style={styles.field}>
                            <label style={styles.label}>Depth</label>
                            <input
                                value={editableProduct.attributes?.dimensions?.depth || ""}
                                onChange={(e) => setDimensionField("depth", e.target.value)}
                                style={styles.input}
                            />
                        </div>

                        <div style={{ ...styles.field, ...styles.full }}>
                            <label style={styles.label}>Features</label>
                            <div style={{ display: "grid", gap: 10 }}>
                                {(editableProduct.features || []).map((feature, index) => (
                                    <div key={`feature-${index}`} style={styles.repeaterRow}>
                                        <input
                                            placeholder="Feature"
                                            value={feature || ""}
                                            onChange={(e) => updateFeature(index, e.target.value)}
                                            style={styles.input}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => removeFeature(index)}
                                            style={styles.button}
                                        >
                                            Remove Feature
                                        </button>
                                    </div>
                                ))}

                                <button
                                    type="button"
                                    onClick={addFeature}
                                    style={styles.button}
                                >
                                    Add Feature
                                </button>
                            </div>
                        </div>

                        <div style={{ ...styles.field, ...styles.full }}>
                            <label style={styles.label}>Generated JSON</label>
                            <textarea
                                readOnly
                                value={jsonOutput}
                                style={styles.jsonBox}
                            />
                        </div>
                    </div>
                </div>
            ) : null}
        </section>
    );
}
